import { router } from '@inertiajs/vue3'

type MatomoCommand = [string, ...(string | number | boolean)[]]

interface MatomoConfig {
  baseUrl: string
  siteId: number
}

let isInitialized = false
let lastTrackedPageViewSignature: string | null = null

const MATOMO_SCRIPT_SELECTOR = 'script[data-matomo-tracker="true"]'

const normalizeBaseUrl = (rawBaseUrl: string): string | null => {
  try {
    const parsedUrl = new URL(rawBaseUrl)
    return parsedUrl.href.endsWith('/') ? parsedUrl.href : `${parsedUrl.href}/`
  } catch {
    return null
  }
}

const resolveConfig = (): MatomoConfig | null => {
  const baseUrl = import.meta.env.VITE_MATOMO_BASE_URL?.trim()
  const siteIdRaw = import.meta.env.VITE_MATOMO_SITE_ID?.trim()

  if (!baseUrl || !siteIdRaw) {
    return null
  }

  const normalizedBaseUrl = normalizeBaseUrl(baseUrl)
  const parsedSiteId = Number.parseInt(siteIdRaw, 10)

  if (!normalizedBaseUrl || Number.isNaN(parsedSiteId) || parsedSiteId <= 0) {
    return null
  }

  return {
    baseUrl: normalizedBaseUrl,
    siteId: parsedSiteId,
  }
}

const ensureScript = (source: string): void => {
  const existingScript = document.querySelector<HTMLScriptElement>(MATOMO_SCRIPT_SELECTOR)

  if (existingScript) {
    return
  }

  const scriptElement = document.createElement('script')
  scriptElement.async = true
  scriptElement.src = source
  scriptElement.dataset.matomoTracker = 'true'
  document.head.appendChild(scriptElement)
}

const getQueue = (): MatomoCommand[] => {
  window._paq = window._paq ?? []
  return window._paq
}

const createPageViewSignature = (url: string, title: string): string => {
  return JSON.stringify([url, title])
}

const trackPageView = (url: string, title: string): void => {
  const signature = createPageViewSignature(url, title)

  if (lastTrackedPageViewSignature === signature) {
    return
  }

  lastTrackedPageViewSignature = signature

  const queue = getQueue()
  queue.push(['setCustomUrl', url])
  queue.push(['setDocumentTitle', title])
  queue.push(['trackPageView'])
}

const getNavigationUrl = (pageUrl: string): string => {
  try {
    return new URL(pageUrl, window.location.origin).toString()
  } catch {
    return window.location.href
  }
}

const trackPageViewAfterTitleUpdate = (url: string): void => {
  const initialTitle = document.title
  let hasTracked = false

  const observer = new MutationObserver(() => {
    if (document.title !== initialTitle) {
      flush()
    }
  })

  const flush = (): void => {
    if (hasTracked) {
      return
    }

    hasTracked = true
    observer.disconnect()
    clearTimeout(fallbackTimeoutId)
    trackPageView(url, document.title)
  }

  const fallbackTimeoutId = window.setTimeout(flush, 50)
  observer.observe(document.head, { childList: true, subtree: true, characterData: true })
}

export const trackMatomoGoal = (goalId: number): void => {
  if (!isInitialized || typeof window === 'undefined' || goalId <= 0) {
    return
  }

  getQueue().push(['trackGoal', goalId])
}

export default (): void => {
  if (isInitialized || typeof window === 'undefined') {
    return
  }

  const config = resolveConfig()
  if (!config) {
    return
  }

  isInitialized = true

  const trackerUrl = `${config.baseUrl}matomo.php`
  const scriptUrl = `${config.baseUrl}matomo.js`
  const queue = getQueue()

  queue.push(['setTrackerUrl', trackerUrl])
  queue.push(['setSiteId', config.siteId])

  ensureScript(scriptUrl)

  trackPageView(window.location.href, document.title)

  router.on('navigate', (event) => {
    const pageUrl = getNavigationUrl(event.detail.page.url)
    trackPageViewAfterTitleUpdate(pageUrl)
  })
}
